<template>
  <Teleport :to="store.overlayTarget">
    <!-- Backdrop -->
    <Transition name="fade">
      <div v-if="open" class="fixed inset-0 bg-black/30 z-[70]" @click="cancel"></div>
    </Transition>

    <!-- Flex-centred wrapper so the card fades and scales in place -->
    <Transition name="pop">
      <div v-if="open" class="fixed inset-0 z-[80] flex items-center justify-center p-4 pointer-events-none">
        <div
          class="pointer-events-auto w-[420px] max-w-full flex flex-col bg-white dark:bg-[#1C2128] rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700"
          role="dialog"
          aria-modal="true"
          :aria-label="heading"
          @click.stop
        >
          <!-- Header -->
          <div class="flex items-center justify-between px-5 h-14 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ heading }}</h3>
            <button
              @click="cancel"
              class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Close"
            >
              <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-5">
            <!-- What is being moved, so the choice has context -->
            <div class="flex items-start gap-3 px-3 py-2.5 mb-4 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
              <span
                class="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1"
                :style="{ backgroundColor: booking?.color_code || '#3b82f6' }"
              ></span>
              <div class="min-w-0">
                <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {{ booking?.title || 'Untitled event' }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ newTimeLabel }}</p>
              </div>
            </div>

            <!-- Step 1: which occurrences the move applies to -->
            <div v-if="step === 'scope'" role="radiogroup" :aria-label="heading" class="space-y-2">
              <button
                v-for="option in SCOPES"
                :key="option.value"
                type="button"
                role="radio"
                :aria-checked="scope === option.value"
                @click="scope = option.value"
                class="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors"
                :class="scope === option.value
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'"
              >
                <span
                  class="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors"
                  :class="scope === option.value ? 'border-blue-600' : 'border-gray-300 dark:border-gray-600'"
                >
                  <span v-if="scope === option.value" class="w-2 h-2 rounded-full bg-blue-600"></span>
                </span>
                <span class="min-w-0">
                  <span class="block text-sm font-medium text-gray-900 dark:text-white">{{ option.label }}</span>
                  <span class="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ option.hint }}</span>
                </span>
              </button>

              <p class="text-xs text-gray-400 dark:text-gray-500 pt-1 leading-relaxed">
                Moving all events shifts every occurrence by the same amount.
              </p>
            </div>

            <!-- Step 2: Google's guest notification prompt -->
            <div v-else>
              <p class="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                {{ guestCount }} {{ guestCount === 1 ? 'guest' : 'guests' }}
                {{ guestCount === 1 ? 'is' : 'are' }} invited to this event. Google can email
                {{ guestCount === 1 ? 'them' : 'them' }} about the new time.
              </p>
              <div class="flex items-start gap-2.5 px-3 py-2.5 mt-4 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                <svg class="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  The event moves either way. This only controls whether Google sends the notification.
                </p>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
            <button
              @click="cancel"
              class="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              Cancel
            </button>

            <button
              v-if="step === 'scope'"
              @click="confirmScope"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Continue
            </button>

            <template v-else>
              <button
                @click="finish('none')"
                class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Don't send
              </button>
              <button
                @click="finish('all')"
                class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Send
              </button>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useCalendarStore }  from 'stores/calendar.js'
import { useGlobalStore }    from 'stores/global.js'

// Mirrors Google Calendar's own two prompts, in the same order: which
// occurrences the move applies to, then whether guests hear about it.
//
// "This and following events" is deliberately absent. Google has no API call
// for it (the master's RRULE has to be rewritten with an UNTIL and a second
// series created), so offering it would be a button that cannot work.

const props = defineProps({
  // { booking, startAt, endAt } while a move is awaiting confirmation.
  // startAt/endAt are wall-clock strings straight from the grid.
  move: { type: Object, default: null },
})

const emit = defineEmits(['confirm', 'cancel'])

const store       = useCalendarStore()
const globalStore = useGlobalStore()

const SCOPES = [
  { value: 'this', label: 'This event',  hint: 'Only this occurrence changes.' },
  { value: 'all',  label: 'All events',  hint: 'Every occurrence in the series moves.' },
]

const open  = computed(() => !!props.move)
const step  = ref('scope')
const scope = ref('this')

const booking     = computed(() => props.move?.booking)
const isRecurring = computed(() => !!booking.value?.recurring_booking_id)
const guestCount  = computed(() => (booking.value?.attendees || []).length)
const hasGuests   = computed(() => guestCount.value > 0)

const heading = computed(() =>
  step.value === 'scope' ? 'Move recurring event' : 'Send update emails?'
)

// The dropped time, formatted from the wall-clock string the grid produced.
// Parsing it into a Date would reintroduce the browser's timezone, which is not
// necessarily the calendar's display timezone.
const newTimeLabel = computed(() => {
  const start = props.move?.startAt
  const end   = props.move?.endAt
  if (!start || !end) return ''

  const is24 = globalStore.user?.time_format === '24_hours'
  const [datePart, startTime] = start.split('T')
  const endTime = end.split('T')[1]

  const [y, m, d] = datePart.split('-').map(Number)
  const dayLabel = new Date(y, m - 1, d)
    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })

  return `${dayLabel}, ${clock(startTime, is24)} to ${clock(endTime, is24)}`
})

function clock(hhmmss, is24) {
  const [h, m] = hhmmss.split(':').map(Number)
  if (is24) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  const suffix = h >= 12 ? 'PM' : 'AM'
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return m === 0 ? `${hour12} ${suffix}` : `${hour12}:${String(m).padStart(2, '0')} ${suffix}`
}

// Open on whichever question actually applies: a one-off event with guests goes
// straight to the notification prompt.
watch(() => props.move, (move) => {
  if (!move) return
  scope.value = 'this'
  step.value  = isRecurring.value ? 'scope' : 'notify'
})

function confirmScope() {
  if (hasGuests.value) {
    step.value = 'notify'
    return
  }
  emit('confirm', { scope: scope.value, notify: 'none' })
}

function finish(notify) {
  emit('confirm', { scope: scope.value, notify })
}

function cancel() {
  emit('cancel')
}

function onKeydown(e) {
  if (e.key === 'Escape' && open.value) cancel()
}
onMounted(()   => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.pop-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.pop-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.pop-enter-from, .pop-leave-to { opacity: 0; transform: scale(0.96); }
</style>
